// We must initialize Sentry to wrap the fetch method *before* importing the
// regen-api-ts-client, which creates the HeyAPI fetch client at import time
// If the api-ts-client is created before Sentry is initialized, none of the
// requests to our api will be captured by Sentry
import "../initSentry";

import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { client } from "regen-api-ts-client";

import { isAuthOverrideValid, useAuthOverride } from "../util/authOverride";

const AUTH_ERROR_STATUS_CODES = [401, 403];

// Note that `throwOnError` is set to true in every generated
// regen-api-ts-client.react-query options - but defaults to false for the
// fetches directly.
client.setConfig({ baseUrl: import.meta.env.VITE_REGEN_ENDPOINT });

// Weird workaround because Auth0 does not support sharing a context
// in an Auth0Provider and also outside of it, and we need to access tokens
// out here as well.
// Configure the generated API client to use our Auth0 token
// See response from Auth0 on request to support this:
// https://github.com/auth0/auth0-react/issues/212
export const ApiContext = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const authOverrideToken = useAuthOverride();

  useEffect(() => {
    const addHeaderCallback = async (request: Request) => {
      // Override the Authorization header if an authOverrideToken is provided
      // and still valid
      if (authOverrideToken && isAuthOverrideValid(authOverrideToken)) {
        request.headers.set(
          "Authorization",
          `Bearer ${authOverrideToken.token}`,
        );
      } else if (getAccessTokenSilently) {
        // Resolve the Auth0 token now, when the call is made.
        const token = await getAccessTokenSilently().catch((e) => {
          console.error("Error getting token", e);
          if (loginWithRedirect) {
            loginWithRedirect();
          }
        });

        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    };
    client.interceptors.request.use(addHeaderCallback);
    return () => {
      client.interceptors.request.eject(addHeaderCallback);
    };
  }, [authOverrideToken, getAccessTokenSilently, loginWithRedirect]);

  useEffect(() => {
    const handleUnautheriorized = async (
      error: unknown,
      response: Response,
    ) => {
      // Send to login if the user isn't logged in
      // If we get a 401 on `/my_user`, then we are not authenticated
      // (401s will occur for new users who are not yet onboarded for other
      // endpoints, '/my_user' is the only one that does not require
      // authorization to request other resources)
      // This holds true for magic links when the user leaves the link url.
      if (response.status === 401 && response.url.includes("/my_user")) {
        await loginWithRedirect();
        return Promise.reject(`Unauthorized: ${response.status} - 
          ${response.url} - ${JSON.stringify(error)}`);
      }
    };
    client.interceptors.error.use(handleUnautheriorized);

    return () => {
      client.interceptors.error.eject(handleUnautheriorized);
    };
  }, [loginWithRedirect]);

  return undefined;
};

// Unfortunately the client error interceptor is of type `unknown`
export type JSONAPIError = Error & {
  type: string;
  title: string;
  status: number;
  detail: string;
};

// Handle any errors coming from the fetch client
// `error` argument is already parsed as json. Refer to the implementation:
// https://github.com/hey-api/openapi-ts/blob/8322b39425766379593e0dda8122350cc08e0da9/packages/client-fetch/src/index.ts#L119
client.interceptors.error.use(async (error: unknown, response, _, options) => {
  // Suppress auth errors, which will be handled by loginWithRedirect.
  // Otherwise, set a toast error.
  if (AUTH_ERROR_STATUS_CODES.includes(response.status)) {
    return error;
  }
  // If the caller has set throwOnError to false, return the error without
  // showing a toast
  // `throwOnError` is one of the only options that makes it through fetch
  // client system end to end, so we are using it as a signal to not show a
  // toast but it still actually throws an error.
  // We expect that in the future, we won't automatically show toasts for any
  // 'errored' request, so this strange misuse of `throwOnError` will be
  // unnecessary.
  // TODO: find a way to handle this correctly with useQuery, which requires
  // the fetch function to actually throw on error.
  //
  // NOTE: The generated *Options() functions do not allow you to pass
  // through throwOnError, it is hardcoded to true, so you'll have to use the
  // `useRegenQuery` hook to get this behavior.
  if (options.throwOnError === false) {
    // Do actually throw the error though, otherwise useQuery will not pass
    // it through and will instead say 'data' is undefined
    throw error;
  }
  // Otherwise pop a toast for this error
  let message;
  try {
    message = (error as JSONAPIError).detail;
  } catch (e) {
    error =
      e instanceof Error
        ? `Unexpected error (${e.message})`
        : "Unexpected error";
  }
  toast.error(message);
  return error;
});
